'use client'

import { type MutableRefObject, useCallback, useRef } from 'react'

import { createAccountDumpTracker } from '@/service/ws/account-dump-tracker'

import { ackResync, currentResyncGeneration } from './resync-acks'
import type { ResyncDomain } from './types'

/**
 * useRef(create()) evaluates create() on every render even though only the
 * first call's result is ever kept — here that's a throwaway Set/object per
 * render. Lazily initializes exactly once instead, keeping the same stable
 * MutableRefObject<T> shape every other useRef() consumer expects.
 */
function useStableRef<T>(create: () => T): MutableRefObject<T> {
  const ref = useRef<T | undefined>(undefined)
  if (ref.current === undefined) {
    ref.current = create()
  }
  return ref as MutableRefObject<T>
}

/**
 * Per-account dump tracking that also acknowledges resync generations.
 *
 * Wraps the existing AccountDumpTracker with two things the recovery system
 * needs:
 *
 * 1. **Lazy generation reset.** The `useEffect` watching triggerResyncAtom is
 *    not a reliable reset point on its own — a WS frame can land before React
 *    flushes that effect, and it would then be matched against the *previous*
 *    generation's tracker state and classified as a delta. So the message path
 *    checks the generation itself and resets on the spot; the effect stays as a
 *    belt-and-braces reset for scope changes.
 *
 * 2. **All-accounts acknowledgement.** The ack fires only once every account in
 *    scope has re-dumped. On a batched channel the accounts interleave, so
 *    acking on the first dump would report the resync complete while other
 *    accounts are still seeding.
 */
export function useResyncDumpTracker(domain: ResyncDomain) {
  const trackerRef = useStableRef(createAccountDumpTracker)
  const seenGenerationRef = useStableRef(currentResyncGeneration)

  /**
   * Call at the top of the message handler, before isInitialDump(). Resets the
   * tracker if a new resync generation started since the last frame.
   */
  const syncGeneration = useCallback(() => {
    const generation = currentResyncGeneration()
    if (generation !== seenGenerationRef.current) {
      seenGenerationRef.current = generation
      trackerRef.current.reset()
    }
    return generation
    // trackerRef/seenGenerationRef come back from useStableRef, not useRef
    // directly, so eslint-plugin-react-hooks can't see through the wrapper to
    // confirm they're ref-stable — they are (see useStableRef), so listing
    // them here is just satisfying the rule, not a real dependency.
  }, [trackerRef, seenGenerationRef])

  /** Call after markDumped(). Acks once the whole scope has re-dumped. */
  const noteDumped = useCallback(
    (accountIds: readonly string[]) => {
      if (accountIds.length === 0) return
      if (!trackerRef.current.hasDumpedAll(accountIds)) return
      ackResync(domain, seenGenerationRef.current)
    },
    [domain, trackerRef, seenGenerationRef]
  )

  /** Call from the existing scope/resync reset effect. */
  const resetTracker = useCallback(() => {
    seenGenerationRef.current = currentResyncGeneration()
    trackerRef.current.reset()
  }, [trackerRef, seenGenerationRef])

  return { trackerRef, syncGeneration, noteDumped, resetTracker }
}
